// Apļa analīze: robainums, plūsmu bilance un prioritātes.

import type { EnergyNow } from './energy';
import { mean, stdev, uniq } from './format';
import type { Index } from './tree';
import type { Link } from './types';

export interface FlowStat {
  outGive: number;
  outTake: number;
  inGive: number;
  inTake: number;
  feeds: Link[];
  fedBy: Link[];
  drains: Link[];
  drainedBy: Link[];
}

/** Plūsmas starp virsjomām (saites jomas iekšienē netiek skaitītas). */
export function flowStats(idx: Index, links: Link[]): Map<string, FlowStat> {
  const m = new Map<string, FlowStat>(
    idx.tops.map((t) => [t, { outGive: 0, outTake: 0, inGive: 0, inTake: 0, feeds: [], fedBy: [], drains: [], drainedBy: [] }])
  );
  links.forEach((l) => {
    const a = idx.top.get(l.fromId);
    const b = idx.top.get(l.toId);
    if (!a || !b || a === b) return;
    const A = m.get(a)!;
    const B = m.get(b)!;
    if (l.type === 'give') {
      A.outGive += l.strength;
      B.inGive += l.strength;
      A.feeds.push(l);
      B.fedBy.push(l);
    } else {
      A.outTake += l.strength;
      B.inTake += l.strength;
      A.drains.push(l);
      B.drainedBy.push(l);
    }
  });
  return m;
}

export interface Priority {
  id: string;
  value: number;
  score: number;
  energyUpside: number;
  feeds: string[];
  drainedBy: string[];
}

/** Prioritāte = trūkums (10 − vērtība) × ietekme. Ietekme aug, ja joma baro citas, ja no tās atņem un ja tās uzlabošana dotu enerģiju. */
export function priorities(idx: Index, links: Link[], en: EnergyNow | null, limit = 5): Priority[] {
  const fs = flowStats(idx, links);
  return idx.tops
    .map((t) => {
      const v = idx.val.get(t)!;
      const f = fs.get(t)!;
      const eu = Math.max(0, en?.up.get(t) || 0);
      const impact = 1 + f.outGive / 2 + f.inTake / 4 + eu / 20;
      return {
        id: t,
        value: v,
        score: (10 - v) * impact,
        energyUpside: eu,
        feeds: uniq(f.feeds.map((l) => idx.top.get(l.toId)!)),
        drainedBy: uniq(f.drainedBy.map((l) => idx.top.get(l.fromId)!))
      };
    })
    .filter((r) => r.score > 0.01)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function balanceStats(idx: Index, threshold: number) {
  const vals = idx.tops.map((t) => idx.val.get(t)!);
  const avg = mean(vals);
  const sd = stdev(vals);
  const low = idx.tops.filter((t) => idx.val.get(t)! <= threshold);
  const verdict =
    sd < 1
      ? { short: 'apaļš', long: 'Aplis ir apaļš — jomas ir līdzsvarā.' }
      : sd < 2
        ? { short: 'nedaudz robains', long: 'Aplis ir nedaudz robains — dažas jomas atpaliek.' }
        : { short: 'izteikti robains', long: 'Aplis ir izteikti robains — ir jomas ar būtisku trūkumu.' };
  const byVal = [...idx.tops].sort((a, b) => idx.val.get(a)! - idx.val.get(b)!);
  return { avg, sd, low, verdict, min: byVal[0] ?? null, max: byVal[byVal.length - 1] ?? null };
}

/** Vājākās apakšjomas (lapas zem virsjomām). */
export function weakestLeaves(idx: Index, n = 5): string[] {
  return Array.from(idx.node.keys())
    .filter((id) => !idx.kids.get(id)!.length && idx.depth.get(id)! > 0)
    .sort((a, b) => idx.val.get(a)! - idx.val.get(b)!)
    .slice(0, n);
}
